import React, { useState, useEffect, useMemo } from 'react'
import { Info, ChevronDown } from 'lucide-react'
import useAudienceTargetingStore from '@/store/audienceTargetingStore'
import { Checkbox } from '@/components/ui/checkbox'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.jsx'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import CalendarIcon from '@/components/icons/CalendarIcon'
import TableRowSkeleton from '@/components/common/TableRowSkeleton'
import MetricsPicker from '../MetricsPicker'
import { useColumnPreferences } from '../hooks/useColumnPreferences'
import { cn } from '@/lib/utils'

const Row = ({
    age,
    isLast,
    checked,
    handleAgeToggle,
    selectedColumns,
    ageMetrics,
    formatMetricValue,
    showConnectorBelow,
}) => {
    let style = {}
    if (isLast) style.borderBottom = '0px solid transparent'

    // Helper function to get metric value from ageMetrics
    const getMetricValue = (apiKey) => {
        const metrics = ageMetrics[age.id] || {}
        return metrics[apiKey] || 0
    }

    return (
        <TableRow
            style={style}
            className={cn('tw:cursor-pointer hover:tw:bg-gray-700/20 tw:relative', {
                'tw:bg-blue-900/10 tw:border-l-2 tw:border-blue-500': checked,
            })}
            onClick={() => handleAgeToggle(age.id)}
        >
            <TableCell className="tw:pl-3 tw:relative">
                <div className="tw:flex tw:items-center tw:gap-3">
                    <div className="tw:relative tw:flex tw:items-center">
                        <Checkbox
                            checked={checked}
                            onClick={(e) => e.stopPropagation()}
                            onCheckedChange={() => handleAgeToggle(age.id)}
                            className="tw:z-10"
                        />
                        {/* Vertical connector line */}
                        {showConnectorBelow && (
                            <div className="tw:absolute tw:left-1/2 tw:top-[100%] tw:-translate-x-1/2 tw:w-0.5 tw:h-[2.1rem] tw:bg-blue-500/50 tw:z-10" />
                        )}
                    </div>
                    <span className="tw:text-sm tw:text-white tw:font-medium">{age.label}</span>
                </div>
            </TableCell>
            {/* Dynamic metric columns */}
            {selectedColumns.map((column) => {
                const value = getMetricValue(column.apiKey)
                const formattedValue = formatMetricValue(value, column)

                return (
                    <TableCell key={column.id} className="tw:text-right tw:text-sm tw:text-white tw:pr-3">
                        {formattedValue}
                    </TableCell>
                )
            })}
        </TableRow>
    )
}

// Move age options outside component to prevent re-creation on every render
const AGE_OPTIONS = [
    { id: '18-24', label: '18-24' },
    { id: '25-34', label: '25-34' },
    { id: '35-44', label: '35-44' },
    { id: '45-54', label: '45-54' },
    { id: '55-64', label: '55-64' },
    { id: '65+', label: '65+' },
]

const AgeSettings = ({
    stage,
    audience,
    metricsDataLoading,
    formatMetricValue,
    metricsData,
    setStepContentData,
    getStepContentData,
}) => {
    const { getAudienceSettings, updateAudienceSetting } = useAudienceTargetingStore()
    const [selectedAges, setSelectedAges] = useState([])

    // Column preferences hook
    const {
        selectedColumns,
        allMetrics,
        updateSelectedColumns,
        isLoading: allMetricsLoading,
    } = useColumnPreferences('Age', setStepContentData, getStepContentData)

    // Handle metric replacement for a specific column index
    const handleMetricReplace = (columnIndex, newMetric) => {
        const currentMetrics = [...selectedColumns]
        currentMetrics[columnIndex] = newMetric
        updateSelectedColumns(currentMetrics)
    }

    // Process metrics data to organize by age range and metric
    const ageMetrics = useMemo(() => {
        const metrics = {}
        if (metricsData) {
            metricsData.forEach((metric) => {
                const ageRange = metric.breakdownValue
                if (!metrics[ageRange]) metrics[ageRange] = {}
                // Store all metric values by their API key
                metrics[ageRange][metric.metricApiKey] = metric.value
            })
        }
        return metrics
    }, [metricsData])

    // Get current settings
    const audienceSettings = getAudienceSettings(stage, audience)
    const storedAges = audienceSettings?.age_targets

    // Initialize from stored value
    useEffect(() => {
        if (storedAges && Array.isArray(storedAges)) {
            setSelectedAges(storedAges)
        } else {
            // Default to all age ranges selected
            const defaultAges = AGE_OPTIONS.map((age) => age.id)
            setSelectedAges(defaultAges)
            updateAudienceSetting(stage, audience, 'age_targets', defaultAges)
        }
    }, [storedAges, stage, audience, updateAudienceSetting])

    // Helper function to get the index of an age range
    const getAgeIndex = (ageId) => {
        return AGE_OPTIONS.findIndex((age) => age.id === ageId)
    }

    // Helper function to fill gaps between selected ranges
    const fillGaps = (currentSelection, newAgeId) => {
        if (currentSelection.length === 0) {
            return [newAgeId]
        }

        const newIndex = getAgeIndex(newAgeId)
        const selectedIndices = currentSelection.map((id) => getAgeIndex(id)).sort((a, b) => a - b)

        const minIndex = Math.min(newIndex, selectedIndices[0])
        const maxIndex = Math.max(newIndex, selectedIndices[selectedIndices.length - 1])

        // Return all age IDs between min and max (inclusive)
        return AGE_OPTIONS.slice(minIndex, maxIndex + 1).map((age) => age.id)
    }

    // Helper function to handle deselection logic
    const smartDeselect = (currentSelection, ageIdToRemove) => {
        const removeIndex = getAgeIndex(ageIdToRemove)
        const selectedIndices = currentSelection.map((id) => getAgeIndex(id)).sort((a, b) => a - b)

        // Find continuous blocks
        const blocks = []
        let currentBlock = [selectedIndices[0]]

        for (let i = 1; i < selectedIndices.length; i++) {
            if (selectedIndices[i] === selectedIndices[i - 1] + 1) {
                currentBlock.push(selectedIndices[i])
            } else {
                blocks.push([...currentBlock])
                currentBlock = [selectedIndices[i]]
            }
        }
        blocks.push(currentBlock)

        // Find which block contains the age to remove
        let blockToKeep = null
        for (const block of blocks) {
            if (block.includes(removeIndex)) {
                // Split this block at the removal point
                const leftPart = block.filter((idx) => idx < removeIndex)
                const rightPart = block.filter((idx) => idx > removeIndex)

                // Keep the larger part, or the left part if equal
                if (leftPart.length >= rightPart.length && leftPart.length > 0) {
                    blockToKeep = leftPart
                } else if (rightPart.length > 0) {
                    blockToKeep = rightPart
                }
                break
            }
        }

        // If no block found (shouldn't happen), keep everything except the removed item
        if (!blockToKeep) {
            return currentSelection.filter((id) => id !== ageIdToRemove)
        }

        // Convert indices back to IDs
        return blockToKeep.map((idx) => AGE_OPTIONS[idx].id)
    }

    const handleAgeToggle = (ageId) => {
        let newSelection
        if (selectedAges.includes(ageId)) {
            // Don't allow deselecting if it's the last one selected
            if (selectedAges.length === 1) return
            // Use smart deselection to maintain continuous ranges
            newSelection = smartDeselect(selectedAges, ageId)
        } else {
            // Fill gaps when selecting a new age range
            newSelection = fillGaps(selectedAges, ageId)
        }

        setSelectedAges(newSelection)
        updateAudienceSetting(stage, audience, 'age_targets', newSelection)
    }

    return (
        <div className="tw:flex tw:gap-6 tw:flex-col tw:bg-secondary-background tw:rounded-xs">
            <div className="tw:flex tw:gap-1.5 tw:items-center tw:p-3 tw:pb-0">
                <CalendarIcon className="tw:w-6 tw:h-6 tw:text-gray-200" />
                <span className="tw:font-semibold">Age</span>
                <Tooltip>
                    <TooltipTrigger>
                        <Info className="tw:w-4 tw:h-4 tw:text-gray-200" />
                    </TooltipTrigger>
                    <TooltipContent showArrow={false}>
                        <div className="tw:text-sm tw:text-gray-400">
                            Target your ads based on age demographics.
                            <br />
                            <span className="tw:text-white tw:font-medium tw:block tw:mt-2">
                                At least one age range must be selected
                            </span>
                        </div>
                    </TooltipContent>
                </Tooltip>
            </div>

            <div className="">
                <Table className="tw:[&_tbody_tr:nth-child(even)]:bg-[#142234]">
                    <TableHeader>
                        <TableRow>
                            <TableHead className="tw:text-[#899CBD] tw:pl-3">Age Range</TableHead>
                            {selectedColumns.map((column, index) => (
                                <TableHead key={column.id} className="tw:text-[#899CBD] tw:!text-right tw:pr-3">
                                    <MetricsPicker
                                        allMetrics={allMetrics}
                                        currentMetric={column}
                                        onMetricSelect={(metric) => handleMetricReplace(index, metric)}
                                    >
                                        <button className="tw:flex tw:items-center tw:justify-end tw:gap-1 hover:tw:text-white tw:transition-colors tw:cursor-pointer tw:w-full">
                                            <span>{column.name}</span>
                                            <div className="tw:flex tw:items-center tw:gap-1">
                                                <ChevronDown className="tw:w-4 tw:h-4" />
                                            </div>
                                        </button>
                                    </MetricsPicker>
                                </TableHead>
                            ))}
                        </TableRow>
                    </TableHeader>
                    {metricsDataLoading || allMetricsLoading ? (
                        <TableRowSkeleton
                            rows={6}
                            showIcon={false}
                            showCheckbox={true}
                            columns={selectedColumns.length + 1}
                        />
                    ) : (
                        <TableBody>
                            {AGE_OPTIONS.map((age, index) => {
                                const isSelected = selectedAges.includes(age.id)
                                const nextAge = AGE_OPTIONS[index + 1]
                                const isNextSelected = nextAge && selectedAges.includes(nextAge.id)
                                const showConnectorBelow = isSelected && isNextSelected

                                return (
                                    <Row
                                        age={age}
                                        key={age.id}
                                        handleAgeToggle={handleAgeToggle}
                                        isLast={AGE_OPTIONS.length - 1 === index}
                                        checked={isSelected}
                                        selectedColumns={selectedColumns}
                                        ageMetrics={ageMetrics}
                                        formatMetricValue={formatMetricValue}
                                        showConnectorBelow={showConnectorBelow}
                                    />
                                )
                            })}
                        </TableBody>
                    )}
                </Table>
            </div>
        </div>
    )
}

export default AgeSettings
